import { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../../components/ui/dialog';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { cn } from '../../lib/utils';
import { ScanLine, Upload, FileText, CheckCircle2, Loader2, Calendar, Users, Clock, Shield, ArrowRight, Sparkles } from 'lucide-react';
import { toast } from 'sonner';
import { guardWrite } from '../../lib/modeGuard';
import { useAppMode } from '../../contexts/AppModeContext';

const SAMPLE_DOCS = {
  appointments: { name: 'daily_schedule_04_17.csv', rows: 12, fields: ['Patient Name', 'Time', 'Provider', 'Type', 'Insurance', 'Copay'] },
  patients: { name: 'patient_roster_export.csv', rows: 245, fields: ['Name', 'DOB', 'MRN', 'Phone', 'Insurance', 'PCP'] },
  providers: { name: 'provider_availability.xlsx', rows: 6, fields: ['Provider', 'Mon-Fri Hours', 'Lunch Block', 'Max Daily', 'Specialties'] },
  insurance: { name: 'insurance_cards_batch.pdf', rows: 8, fields: ['Patient', 'Payer', 'Member ID', 'Group', 'Status'] },
};

const IMPORT_TYPES = [
  { id: 'appointments', label: 'Appointment Summary', desc: 'Daily/weekly schedules from your EHR', icon: Calendar, color: 'text-blue-600 bg-blue-50' },
  { id: 'patients', label: 'Patient Roster', desc: 'Demographics and insurance data', icon: Users, color: 'text-violet-600 bg-violet-50' },
  { id: 'providers', label: 'Provider Schedule', desc: 'Availability templates', icon: Clock, color: 'text-emerald-600 bg-emerald-50' },
  { id: 'insurance', label: 'Insurance Cards', desc: 'Batch ID card scans', icon: Shield, color: 'text-amber-600 bg-amber-50' },
];

const LensUploadLightbox = ({ open, onOpenChange }) => {
  const { isDemo } = useAppMode();
  const [stage, setStage] = useState('select'); // select | uploading | preview | done
  const [selectedType, setSelectedType] = useState(null);
  const [progress, setProgress] = useState(0);

  const startUpload = (type) => {
    setSelectedType(type);
    setStage('uploading');
    setProgress(0);
    const interval = setInterval(() => {
      setProgress(prev => {
        if (prev >= 100) { clearInterval(interval); setTimeout(() => setStage('preview'), 300); return 100; }
        return prev + Math.random() * 15 + 5;
      });
    }, 200);
  };

  const confirmImport = () => {
    guardWrite('Import schedule data', () => {
      setStage('done');
      toast.success('Import complete', { description: `${SAMPLE_DOCS[selectedType]?.rows} records imported` });
    }, isDemo);
  };

  const reset = () => { setStage('select'); setSelectedType(null); setProgress(0); };

  const handleClose = () => { reset(); onOpenChange(false); };

  const doc = selectedType ? SAMPLE_DOCS[selectedType] : null;
  const typeInfo = IMPORT_TYPES.find(t => t.id === selectedType);

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-lg p-0 gap-0 overflow-hidden" data-testid="lens-upload-lightbox">
        <DialogHeader className="px-6 pt-5 pb-3">
          <div className="flex items-center gap-2">
            <div className="h-8 w-8 rounded-lg bg-violet-100 flex items-center justify-center">
              <ScanLine className="h-4 w-4 text-violet-600" />
            </div>
            <div>
              <DialogTitle className="text-base font-semibold">Upload with Lens</DialogTitle>
              <DialogDescription className="text-xs">Import data from your existing EHR system</DialogDescription>
            </div>
          </div>
        </DialogHeader>

        {/* ═══ SELECT TYPE ═══ */}
        {stage === 'select' && (
          <div className="px-6 pb-5 space-y-2" data-testid="lens-select-type">
            {IMPORT_TYPES.map(type => (
              <button key={type.id} onClick={() => startUpload(type.id)}
                className="w-full flex items-center gap-3 p-3 rounded-lg border border-slate-100 hover:border-slate-300 hover:bg-slate-50 transition-colors text-left"
                style={{ cursor: 'pointer' }} data-testid={`lens-type-${type.id}`}>
                <div className={cn("h-9 w-9 rounded-lg flex items-center justify-center", type.color.split(' ')[1])}>
                  <type.icon className={cn("h-4 w-4", type.color.split(' ')[0])} />
                </div>
                <div className="flex-1">
                  <p className="text-sm font-medium text-slate-800">{type.label}</p>
                  <p className="text-[10px] text-slate-400">{type.desc}</p>
                </div>
                <ArrowRight className="h-3.5 w-3.5 text-slate-300" />
              </button>
            ))}
            <p className="text-[9px] text-slate-400 text-center pt-1">Supports CSV, XLSX, PDF, HL7, and CCD/C-CDA formats</p>
          </div>
        )}

        {/* ═══ UPLOADING ═══ */}
        {stage === 'uploading' && (
          <div className="px-6 pb-5 text-center space-y-4" data-testid="lens-uploading">
            <Loader2 className="h-8 w-8 text-violet-600 mx-auto animate-spin" />
            <div>
              <p className="text-sm font-medium text-slate-800">Processing {doc?.name}</p>
              <p className="text-xs text-slate-400 mt-0.5">
                {progress < 40 ? 'Scanning document...' : progress < 70 ? 'Extracting fields...' : 'Validating data...'}
              </p>
            </div>
            <div className="w-48 mx-auto bg-slate-200 rounded-full h-1.5 overflow-hidden">
              <div className="bg-violet-600 h-full rounded-full transition-all duration-300" style={{ width: `${Math.min(progress, 100)}%` }} />
            </div>
          </div>
        )}

        {/* ═══ PREVIEW ═══ */}
        {stage === 'preview' && doc && (
          <div className="px-6 pb-5 space-y-3" data-testid="lens-preview">
            <div className="flex items-center gap-2 p-2 rounded-lg bg-emerald-50">
              <CheckCircle2 className="h-4 w-4 text-emerald-600" />
              <span className="text-xs text-emerald-700 font-medium">Successfully parsed {doc.rows} records</span>
            </div>
            <div className="p-3 rounded-lg border border-slate-200">
              <div className="flex items-center gap-2 mb-2">
                <FileText className="h-3.5 w-3.5 text-slate-400" />
                <span className="text-xs font-medium text-slate-800">{doc.name}</span>
                <Badge className="text-[8px] border-0 bg-slate-100 text-slate-500">{doc.rows} rows</Badge>
              </div>
              <p className="text-[10px] font-semibold text-slate-400 uppercase mb-1.5">Detected fields</p>
              <div className="flex flex-wrap gap-1">
                {doc.fields.map((f, i) => (
                  <Badge key={i} variant="outline" className="text-[9px]">{f}</Badge>
                ))}
              </div>
            </div>
            <div className="rounded-lg border border-slate-200 overflow-hidden">
              <div className="grid gap-0 text-[9px] font-semibold text-slate-400 uppercase bg-slate-50 px-3 py-1.5" style={{ gridTemplateColumns: `repeat(${Math.min(doc.fields.length, 4)}, 1fr)` }}>
                {doc.fields.slice(0, 4).map((f, i) => <span key={i}>{f}</span>)}
              </div>
              {[0, 1, 2].map(row => (
                <div key={row} className="grid gap-0 text-[10px] text-slate-600 px-3 py-1.5 border-t border-slate-50" style={{ gridTemplateColumns: `repeat(${Math.min(doc.fields.length, 4)}, 1fr)` }}>
                  {doc.fields.slice(0, 4).map((f, i) => (
                    <span key={i} className="truncate">
                      {/* Demo-only sample row content for the Lens preview.
                          Real mode renders empty cells until the OCR backend
                          is wired. */}
                      {isDemo && selectedType === 'appointments' && ['John Smith', '9:00 AM', 'Dr. Chen', 'Follow-up'][i]}
                      {isDemo && selectedType === 'patients' && ['Maria Garcia', '07/22/1985', 'MRN-20198', '(555) 234-5678'][i]}
                      {isDemo && selectedType === 'providers' && ['Dr. Chen', '8-5 PM', '12-1 PM', '16'][i]}
                      {isDemo && selectedType === 'insurance' && ['Emily Davis', 'Aetna HMO', 'AET-554821', 'Active'][i]}
                      {!isDemo && '—'}
                    </span>
                  ))}
                </div>
              ))}
              <div className="px-3 py-1 bg-slate-50 text-[9px] text-slate-400">...and {doc.rows - 3} more rows</div>
            </div>
            <DialogFooter className="gap-2 pt-1">
              <Button variant="outline" size="sm" className="text-xs" onClick={reset}>Cancel</Button>
              <Button size="sm" className="text-xs bg-[#111] hover:bg-[#222] gap-1" onClick={confirmImport} data-testid="lens-confirm-import">
                <Sparkles className="h-3 w-3" /> Import {doc.rows} Records
              </Button>
            </DialogFooter>
          </div>
        )}

        {/* ═══ DONE ═══ */}
        {stage === 'done' && (
          <div className="px-6 pb-5 text-center space-y-4" data-testid="lens-done">
            <CheckCircle2 className="h-10 w-10 text-emerald-500 mx-auto" />
            <div>
              <p className="text-sm font-semibold text-slate-800">Import Complete</p>
              <p className="text-xs text-slate-400 mt-1">{doc?.rows} records from {doc?.name} have been imported</p>
            </div>
            <div className="flex gap-2 justify-center">
              <Button variant="outline" size="sm" className="text-xs" onClick={() => { reset(); }}>Import Another</Button>
              <Button size="sm" className="text-xs bg-[#111] hover:bg-[#222]" onClick={handleClose}>Done</Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default LensUploadLightbox;
